// Every active satellite around the viewer's Earth, where it is at the
// simulation's moment: Starlink's shells, OneWeb, Kuiper, the navigation
// constellations, the stations and the geostationary belt, from CelesTrak's
// element sets (src/satellites/catalog.js) run through SGP4 in a worker
// (src/satellites/worker.js).
//
// Frame. The points hang in a group that copies the Earth mesh's transform
// inside its tilt group, so their local frame is Earth-fixed: +X Greenwich,
// +Y the north pole, -Z longitude 90 E (src/orbits/earthframe.js). The
// shader runs each satellite's orbit forward in TEME, turns it into
// Earth-fixed coordinates by Greenwich sidereal time and maps
// (x, y, z)_ecef -> (x, z, -y), so a satellite over a city is over that
// city on the drawn globe. Distances are in the mesh's own units (its
// radius stands for 6371 km), so altitudes scale with the drawn Earth in
// artistic and realistic modes alike.
//
// Cost. One Points draw for all of them; the worker answers about once a
// second of real time (or every quarter hour of simulated time), and
// nothing runs while the layer is off or the camera is far from Earth.
//
// main.js creates it once (createRealSatellites) and calls update() each
// frame; window.__satellites exposes the toggle for the sidebar and tests.

import * as THREE from 'three';
import { SAT_GROUPS } from './catalog.js';
import { vt } from '../i18n/viewer.js';

const STRIDE = 9;
const EARTH_KM = 6371;
const FAR_RADII = 600;          // beyond this the layer sleeps
const REFRESH_REAL_MS = 1000;   // fresh SGP4 at least this often while time runs
const REFRESH_SIM_S = 900;      // or after this much simulated time
const STATIONS = SAT_GROUPS.findIndex((g) => g.key === 'stations');
// Drawn by main.js as NASA's models on their own SGP4 orbits: the ISS and Hubble.
const MODEL_NORADS = new Set([25544, 20580]);
const STORE_KEY = 'oss.satellites';

const VERT = /* glsl */`
uniform float uDt;
uniform float uGmst;
uniform float uKm;
uniform float uEarthR;
uniform vec3 uSunDir;
uniform float uSize;
uniform float uPixelRatio;
uniform float uMask[${SAT_GROUPS.length}];
uniform vec3 uColors[${SAT_GROUPS.length}];
attribute vec3 aP;
attribute vec3 aQ;
attribute vec3 aOrb;
attribute float aGroup;
varying vec3 vColor;
varying float vAlpha;
const float TAU = 6.28318530718;
void main() {
  int g = int(aGroup + 0.5);
  float ecc = aOrb.x;
  if (ecc < 0.0 || uMask[g] < 0.5) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    vColor = vec3(0.0);
    vAlpha = 0.0;
    return;
  }
  // Two-body motion from the worker's SGP4 state: Kepler's equation by Newton.
  float M = mod(aOrb.y + aOrb.z * uDt, TAU);
  float E = M + ecc * sin(M);
  for (int k = 0; k < 6; k++) E -= (E - ecc * sin(E) - M) / (1.0 - ecc * cos(E));
  vec3 teme = (cos(E) - ecc) * aP + sin(E) * aQ;
  // TEME -> Earth-fixed (turn by -GMST about the pole), then the mesh axes.
  float c = cos(uGmst);
  float s = sin(uGmst);
  vec3 ecf = vec3(c * teme.x + s * teme.y, -s * teme.x + c * teme.y, teme.z);
  vec3 p = vec3(ecf.x, ecf.z, -ecf.y) * uKm;
  // Satellites shine by sunlight: inside Earth's shadow they all but vanish.
  float along = dot(p, uSunDir);
  float off = length(p - along * uSunDir);
  float lit = along < 0.0 ? smoothstep(uEarthR * 0.985, uEarthR * 1.015, off) : 1.0;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  gl_PointSize = uSize * uPixelRatio * (g == ${STATIONS} ? 1.9 : 1.0);
  vColor = uColors[g];
  vAlpha = mix(0.14, 1.0, lit);
}
`;

const FRAG = /* glsl */`
uniform float uIntensity;
varying vec3 vColor;
varying float vAlpha;
void main() {
  float r = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.12, r) * vAlpha;
  if (a < 0.01) discard;
  gl_FragColor = vec4(vColor * uIntensity, a);
}
`;

// Greenwich mean sidereal time (IAU 1982, as SGP4 and satellite.js use it)
// in radians, for a Julian date in UTC.
function gmst(jd) {
  const T = (jd - 2451545.0) / 36525;
  let s = -6.2e-6 * T * T * T + 0.093104 * T * T + (876600 * 3600 + 8640184.812866) * T + 67310.54841;
  s = ((s * Math.PI / 43200) % (2 * Math.PI));
  return s < 0 ? s + 2 * Math.PI : s;
}

const CSS = `
#satellites-groups { display: flex; flex-direction: row; flex-wrap: wrap; align-items: center; gap: 4px; padding: 2px 0 8px; width: 0; min-width: 100%; }
#satellites-groups[hidden] { display: none; }
.sat-chip { display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 0 10px; border-radius: 8px; border: none;
  background: #141922; color: #a8a396; font: 500 12px/1 Inter, sans-serif; letter-spacing: 0; cursor: pointer; transition: background-color .15s ease, color .15s ease; }
.sat-chip:hover { background: #1b212c; color: #ece7da; }
.sat-chip:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.sat-chip i { width: 7px; height: 7px; border-radius: 50%; opacity: 0.35; }
.sat-chip[aria-pressed="true"] { color: #ece7da; background: #1b212c; }
.sat-chip[aria-pressed="true"] i { opacity: 1; }
#satellite-tip { position: fixed; z-index: 60; pointer-events: none; padding: 8px 12px; border-radius: 8px; background: #0a0d13;
  border: none; color: #ece7da; font: 12px/1.45 Inter, -apple-system, sans-serif; white-space: nowrap; font-variant-numeric: tabular-nums; }
#satellite-tip b { display: block; font-weight: 500; font-size: 13px; }
#satellite-tip span { color: #a8a396; }
#satellite-tip[hidden] { display: none; }
`;

export function createRealSatellites({ getEarth, getSun, getSimJD, camera, domElement, isSuppressed = () => false }) {
  let enabled = false;
  let catalog = null;          // { groups, sats }
  let loading = null;
  let worker = null;
  let workerReady = false;
  let pending = false;
  let spare = null;            // the array the GPU let go of, sent back to the worker
  let t0 = 0;                  // the Julian date the current elements were made for
  let lastRequestAt = 0;
  let points = null;
  let group = null;
  let elements = null;         // the InterleavedBuffer the shader reads
  let awake = false;
  const mask = SAT_GROUPS.map(() => 1);
  const _v = new THREE.Vector3();
  const _sun = new THREE.Vector3();
  const _earth = new THREE.Vector3();

  if (!document.getElementById('satellites-css')) {
    const style = document.createElement('style');
    style.id = 'satellites-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // Sidebar toggle and group chips, placed after the constellations toggle
  // until the sidebar puts them where it wants (they are found by id).
  const row = document.createElement('div');
  row.className = 'control-row';
  row.id = 'satellites-row';
  row.innerHTML = `<button id="satellites-toggle" class="audio-btn" type="button">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="9" y="9" width="6" height="6" rx="1"></rect><path d="M9 12H3M21 12h-6M6 9v6M18 9v6"></path><path d="M14.5 6.5a4 4 0 0 1 3 3"></path>
      </svg>
      <span id="satellites-label">SATELLITES OFF</span>
    </button>`;
  const button = row.querySelector('button');
  const label = row.querySelector('#satellites-label');
  button.title = vt('controls.satellites.title');
  const chips = document.createElement('div');
  chips.className = 'control-row';
  chips.id = 'satellites-groups';
  chips.hidden = true;
  SAT_GROUPS.forEach((g, i) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'sat-chip';
    chip.setAttribute('aria-pressed', 'true');
    chip.innerHTML = `<i style="background:#${g.color.toString(16).padStart(6, '0')}"></i>${g.label}`;
    chip.addEventListener('click', () => setGroupVisible(g.key, mask[i] === 0));
    chips.appendChild(chip);
  });
  const anchor = document.getElementById('constellations-toggle');
  const anchorRow = anchor && anchor.closest('.control-row');
  if (anchorRow) anchorRow.after(row, chips);
  else (document.getElementById('controls-panel-scroll') || document.getElementById('controls-panel') || document.body).append(row, chips);
  button.addEventListener('click', () => setVisible(!enabled));

  const tip = document.createElement('div');
  tip.id = 'satellite-tip';
  tip.hidden = true;
  document.body.appendChild(tip);

  function renderLabel() {
    button.classList.toggle('playing', enabled);
    const n = catalog ? catalog.sats.length : 0;
    label.textContent = !enabled ? vt('controls.satellites.off') : loading && !catalog ? vt('controls.satellites.loading') : vt('controls.satellites.count', { count: n });
    chips.hidden = !enabled;
  }

  async function load() {
    if (catalog || loading) return loading;
    loading = (async () => {
      let data = null;
      try {
        const res = await fetch('/api/satellites');
        if (res.ok && /json/.test(res.headers.get('content-type') || '')) data = await res.json();
      } catch { /* the snapshot below */ }
      if (!data || !Array.isArray(data.sats) || data.sats.length < 100) {
        const res = await fetch('/data/satellites.json');
        data = await res.json();
      }
      // The ISS and Hubble are the NASA models main.js already flies.
      data.sats = data.sats.filter((r) => !MODEL_NORADS.has(r[0]));
      catalog = data;
      build();
      worker = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
      worker.onmessage = onWorker;
      worker.postMessage({ type: 'load', rows: data.sats });
      renderLabel();
    })().catch((err) => {
      console.warn('[satellites] could not load the catalogue', err);
      loading = null;
      enabled = false;
      renderLabel();
    });
    return loading;
  }

  function build() {
    const n = catalog.sats.length;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    elements = new THREE.InterleavedBuffer(new Float32Array(n * STRIDE), STRIDE);
    elements.setUsage(THREE.DynamicDrawUsage);
    // Until the first answer every satellite is hidden (e = -1).
    for (let k = 0; k < n; k++) elements.array[k * STRIDE + 6] = -1;
    geometry.setAttribute('aP', new THREE.InterleavedBufferAttribute(elements, 3, 0));
    geometry.setAttribute('aQ', new THREE.InterleavedBufferAttribute(elements, 3, 3));
    geometry.setAttribute('aOrb', new THREE.InterleavedBufferAttribute(elements, 3, 6));
    const groups = new Float32Array(n);
    for (let k = 0; k < n; k++) groups[k] = catalog.sats[k][1];
    geometry.setAttribute('aGroup', new THREE.BufferAttribute(groups, 1));
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uDt: { value: 0 },
        uGmst: { value: 0 },
        uKm: { value: 1 / EARTH_KM },
        uEarthR: { value: 1 },
        uSunDir: { value: new THREE.Vector3(1, 0, 0) },
        uSize: { value: 2.3 },
        uPixelRatio: { value: Math.min(2, window.devicePixelRatio || 1) },
        uMask: { value: mask.slice() },
        uColors: { value: SAT_GROUPS.map((g) => new THREE.Color(g.color)) },
        uIntensity: { value: 0.95 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    points = new THREE.Points(geometry, material);
    points.name = 'realSatellites';
    points.frustumCulled = false;
    points.renderOrder = 2;
    group = new THREE.Group();
    group.name = 'realSatellitesFrame';
    group.add(points);
    group.visible = false;
  }

  function onWorker(ev) {
    const msg = ev.data;
    if (msg.type === 'loaded') { workerReady = true; return; }
    if (msg.type !== 'elements' || !elements) return;
    pending = false;
    // Swap: the shader reads the new orbits from the same moment t0 as the
    // simulation clock is measured against, so nothing jumps.
    spare = elements.array;
    elements.array = msg.buffer;
    elements.needsUpdate = true;
    t0 = msg.jd;
  }

  function request(jd) {
    if (!worker || !workerReady || pending) return;
    pending = true;
    lastRequestAt = performance.now();
    const buffer = spare && spare.length === catalog.sats.length * STRIDE ? spare : undefined;
    spare = null;
    worker.postMessage({ type: 'propagate', jd, buffer }, buffer ? [buffer.buffer] : []);
  }

  function earthMesh() {
    const earth = getEarth && getEarth();
    const tilt = earth && earth.getObjectByName('tiltGroup');
    const mesh = tilt && tilt.children.find((c) => c.isMesh);
    return mesh ? { earth, tilt, mesh } : null;
  }

  let meshRadius = 0;
  function update() {
    if (!enabled || !points) { if (group) group.visible = false; awake = false; return; }
    const found = earthMesh();
    if (!found || isSuppressed()) { group.visible = false; awake = false; return; }
    const { tilt, mesh } = found;
    if (group.parent !== tilt) tilt.add(group);
    if (!meshRadius) {
      const geo = mesh.geometry;
      meshRadius = (geo.parameters && geo.parameters.radius) || (geo.boundingSphere || (geo.computeBoundingSphere(), geo.boundingSphere)).radius;
    }
    group.position.copy(mesh.position);
    group.quaternion.copy(mesh.quaternion);
    group.scale.copy(mesh.scale);
    group.updateMatrixWorld(true);

    // Asleep far from Earth: the shells would be a few pixels anyway.
    group.getWorldPosition(_earth);
    const worldR = meshRadius * group.matrixWorld.getMaxScaleOnAxis();
    const radii = camera.position.distanceTo(_earth) / worldR;
    awake = radii < FAR_RADII;
    group.visible = awake;
    if (!awake) { tip.hidden = true; return; }

    const jd = getSimJD();
    const u = points.material.uniforms;
    if (!t0) { request(jd); return; }
    const dtSim = (jd - t0) * 86400;
    if (Math.abs(dtSim) > REFRESH_SIM_S || (Math.abs(dtSim) > 0.5 && performance.now() - lastRequestAt > REFRESH_REAL_MS)) request(jd);
    // Dots shrink and dim with distance so the shells stay a haze over
    // the globe from afar and read as single satellites up close.
    const t = THREE.MathUtils.clamp(Math.log(Math.max(radii, 1) / 1.5) / Math.log(8), 0, 1);
    u.uSize.value = 2.4 - 1.35 * t;
    u.uIntensity.value = 0.9 - 0.35 * t;
    u.uDt.value = dtSim;
    u.uGmst.value = gmst(jd);
    u.uKm.value = meshRadius / EARTH_KM;
    u.uEarthR.value = meshRadius;
    const sun = getSun && getSun();
    if (sun) sun.getWorldPosition(_sun); else _sun.set(0, 0, 0);
    u.uSunDir.value.copy(group.worldToLocal(_sun)).normalize();
  }

  // Hover: the same orbits run on the CPU for the few milliseconds a
  // pointer move needs, nearest satellite within 10 px that Earth does not hide.
  let hoverAt = 0;
  domElement.addEventListener('pointermove', (ev) => {
    if (!enabled || !awake || !elements || !t0) { tip.hidden = true; return; }
    const now = performance.now();
    if (now - hoverAt < 60) return;
    hoverAt = now;
    const hit = pick(ev.clientX, ev.clientY);
    if (!hit) { tip.hidden = true; return; }
    const g = SAT_GROUPS[catalog.sats[hit.k][1]];
    tip.innerHTML = `<b>${escapeHtml(catalog.sats[hit.k][2])}</b>${g.label} · ${Math.round(hit.altKm).toLocaleString('en-US')} km up · ${hit.speed.toFixed(1)} km/s<br><span>NORAD ${catalog.sats[hit.k][0]}</span>`;
    tip.style.left = `${ev.clientX + 14}px`;
    tip.style.top = `${ev.clientY + 14}px`;
    tip.hidden = false;
  }, { passive: true });
  domElement.addEventListener('pointerleave', () => { tip.hidden = true; });

  // Earth-fixed km at the current moment for satellite k, into out[0..2].
  function ecefKm(k, jd, out) {
    const a = elements.array;
    const o = k * STRIDE;
    const ecc = a[o + 6];
    if (ecc < 0) return false;
    const M = a[o + 7] + a[o + 8] * (jd - t0) * 86400;
    let E = M + ecc * Math.sin(M);
    for (let i = 0; i < 6; i++) E -= (E - ecc * Math.sin(E) - M) / (1 - ecc * Math.cos(E));
    const ce = Math.cos(E) - ecc, se = Math.sin(E);
    const x = ce * a[o] + se * a[o + 3], y = ce * a[o + 1] + se * a[o + 4], z = ce * a[o + 2] + se * a[o + 5];
    const th = gmst(jd), c = Math.cos(th), s = Math.sin(th);
    out[0] = c * x + s * y; out[1] = -s * x + c * y; out[2] = z;
    return true;
  }

  const _km = [0, 0, 0];
  const _cam = new THREE.Vector3();
  function pick(cx, cy) {
    const rect = domElement.getBoundingClientRect();
    const mx = cx - rect.left, my = cy - rect.top;
    const jd = getSimJD();
    const m = group.matrixWorld.elements;
    const scale = meshRadius / EARTH_KM;
    const worldR = meshRadius * group.matrixWorld.getMaxScaleOnAxis();
    group.getWorldPosition(_earth);
    _cam.copy(camera.position);
    const best = { k: -1, d2: 100 };
    for (let k = 0; k < catalog.sats.length; k++) {
      if (!mask[catalog.sats[k][1]] || !ecefKm(k, jd, _km)) continue;
      const lx = _km[0] * scale, ly = _km[2] * scale, lz = -_km[1] * scale;
      _v.set(m[0] * lx + m[4] * ly + m[8] * lz + m[12], m[1] * lx + m[5] * ly + m[9] * lz + m[13], m[2] * lx + m[6] * ly + m[10] * lz + m[14]);
      const wx = _v.x, wy = _v.y, wz = _v.z;
      _v.project(camera);
      if (_v.z < -1 || _v.z > 1) continue;
      const sx = (_v.x + 1) / 2 * rect.width, sy = (1 - _v.y) / 2 * rect.height;
      const d2 = (sx - mx) ** 2 + (sy - my) ** 2;
      if (d2 >= best.d2) continue;
      // Hidden behind Earth: the sight line meets the sphere first.
      const dx = wx - _cam.x, dy = wy - _cam.y, dz = wz - _cam.z;
      const len = Math.hypot(dx, dy, dz);
      const ux = dx / len, uy = dy / len, uz = dz / len;
      const ox = _cam.x - _earth.x, oy = _cam.y - _earth.y, oz = _cam.z - _earth.z;
      const bq = ox * ux + oy * uy + oz * uz;
      const disc = bq * bq - (ox * ox + oy * oy + oz * oz - worldR * worldR);
      if (disc > 0 && -bq - Math.sqrt(disc) > 0 && -bq - Math.sqrt(disc) < len) continue;
      best.k = k; best.d2 = d2;
    }
    if (best.k < 0) return null;
    ecefKm(best.k, jd, _km);
    const r = Math.hypot(_km[0], _km[1], _km[2]);
    // Height over the WGS84 ellipsoid, near enough for a label.
    const lat = Math.asin(_km[2] / r);
    const altKm = r - 6378.137 * (1 - (1 / 298.257) * Math.sin(lat) ** 2);
    const o = best.k * STRIDE;
    const n = elements.array[o + 8];
    const aKm = Math.cbrt(398600.4418 / (n * n));
    const speed = Math.sqrt(398600.4418 * Math.max(0, 2 / r - 1 / aKm));
    return { k: best.k, altKm, speed };
  }

  function setVisible(on) {
    enabled = Boolean(on);
    try { localStorage.setItem(STORE_KEY, enabled ? '1' : '0'); } catch { /* private window */ }
    if (!enabled) tip.hidden = true;
    renderLabel();
    if (enabled) load();
  }

  function setGroupVisible(key, on) {
    const i = SAT_GROUPS.findIndex((g) => g.key === key);
    if (i < 0) return;
    mask[i] = on ? 1 : 0;
    chips.children[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    if (points) points.material.uniforms.uMask.value = mask.slice();
  }

  const api = {
    setVisible,
    toggle: () => setVisible(!enabled),
    isVisible: () => enabled,
    setGroupVisible,
    groups: SAT_GROUPS.map((g) => ({ key: g.key, label: g.label })),
    element: row,
    groupsElement: chips,
    update,
    // For tests: the catalogue, and a satellite's Earth-fixed km now.
    get count() { return catalog ? catalog.sats.length : 0; },
    get ready() { return Boolean(t0); },
    debugEcef(norad) {
      if (!catalog || !t0) return null;
      const k = catalog.sats.findIndex((r) => r[0] === norad);
      if (k < 0 || !ecefKm(k, getSimJD(), _km)) return null;
      return { name: catalog.sats[k][2], ecefKm: _km.slice(), jd: getSimJD() };
    },
    get points() { return points; },
  };
  window.__satellites = api;

  let start = false;
  try { start = localStorage.getItem(STORE_KEY) === '1'; } catch { /* private window */ }
  if (/[?&]satellites=1\b/.test(location.search)) start = true;
  if (start) setVisible(true); else renderLabel();
  return api;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
